import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CONSENT_REQUIRED_COOKIE } from '@/lib/consent';

export type ConsentStatus = 'unknown' | 'granted' | 'denied';
export type ConsentLevel = 'essential' | 'analytics' | 'marketing';

const STORAGE_KEY = 'foxbox_cookie_consent_v1';

// Set by the edge proxy from the visitor's country. Missing cookie means
// we could not geolocate, so we assume consent is required.
const readConsentRequired = (): boolean => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_REQUIRED_COOKIE}=([^;]*)`));
    return match ? match[1] !== '0' : true;
  } catch {
    return true;
  }
};

// Global Privacy Control: a browser-level opt-out signal that California,
// Colorado and Connecticut require us to honour. It outranks the opt-out
// default — a visitor sending it has already declined.
const readGlobalPrivacyControl = (): boolean => {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
};

interface ConsentContextValue {
  status: ConsentStatus;
  level: ConsentLevel | null;
  canUseAnalytics: boolean;
  canUseMarketing: boolean;
  isResolved: boolean;
  // False until the cookie and localStorage have been read on the client.
  isHydrated: boolean;
  // True in opt-in jurisdictions, where nothing may run before a choice.
  isConsentRequired: boolean;
  // True when the browser sent a Global Privacy Control signal.
  isGpcEnabled: boolean;
  // True only once the visitor has chosen for themselves. An implicit
  // opt-out-jurisdiction default does not count.
  hasExplicitChoice: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  grant: () => void;
  deny: () => void;
  chooseEssential: () => void;
  chooseAnalytics: () => void;
  chooseMarketing: () => void;
}

const ConsentContext = createContext<ConsentContextValue>({
  status: 'unknown',
  level: null,
  canUseAnalytics: false,
  canUseMarketing: false,
  isResolved: false,
  isHydrated: false,
  isConsentRequired: true,
  isGpcEnabled: false,
  hasExplicitChoice: false,
  isPreferencesOpen: false,
  openPreferences: () => undefined,
  closePreferences: () => undefined,
  grant: () => undefined,
  deny: () => undefined,
  chooseEssential: () => undefined,
  chooseAnalytics: () => undefined,
  chooseMarketing: () => undefined,
});

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [level, setLevel] = useState<ConsentLevel | null>(null);
  const [consentRequired, setConsentRequired] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isGpcEnabled, setIsGpcEnabled] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    setConsentRequired(readConsentRequired());
    setIsGpcEnabled(readGlobalPrivacyControl());
    setIsHydrated(true);

    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === 'essential' || saved === 'analytics' || saved === 'marketing') {
        setLevel(saved);
        return;
      }
      // Backward compatibility for old binary values.
      if (saved === 'granted') {
        setLevel('marketing');
      } else if (saved === 'denied') {
        setLevel('essential');
      }
    } catch {
      // private mode / disabled storage — leave as 'unknown'
    }
  }, []);

  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);

  const persist = useCallback((next: ConsentLevel) => {
    setLevel(next);
    setIsPreferencesOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(() => {
    // Outside opt-in jurisdictions the law is opt-out, so consent defaults to
    // granted and no banner is shown. It has to be the full 'marketing' level:
    // anything less would silently disable HubSpot chat and forms for the US,
    // which is where most of the traffic is.
    // A GPC signal is an explicit opt-out, so it wins over the opt-out default
    // — but never over a choice the visitor made here themselves.
    const implicitLevel: ConsentLevel | null = isGpcEnabled ? 'essential' : consentRequired ? null : 'marketing';
    const effectiveLevel: ConsentLevel | null = level ?? implicitLevel;
    const status: ConsentStatus =
      effectiveLevel === null ? 'unknown' : effectiveLevel === 'essential' ? 'denied' : 'granted';

    return {
      status,
      level: effectiveLevel,
      canUseAnalytics: effectiveLevel === 'analytics' || effectiveLevel === 'marketing',
      canUseMarketing: effectiveLevel === 'marketing',
      isResolved: effectiveLevel !== null,
      isHydrated,
      isConsentRequired: consentRequired,
      isGpcEnabled,
      hasExplicitChoice: level !== null,
      isPreferencesOpen,
      openPreferences,
      closePreferences,
      grant: () => persist('marketing'),
      deny: () => persist('essential'),
      chooseEssential: () => persist('essential'),
      chooseAnalytics: () => persist('analytics'),
      chooseMarketing: () => persist('marketing'),
    };
  }, [level, consentRequired, isHydrated, isGpcEnabled, isPreferencesOpen, openPreferences, closePreferences, persist]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
